/* eslint-disable no-restricted-syntax -- The admin's own design system (shared/rd), ported from the Restaurant Dashboard design; admin chose app-local components over @a2z/ui-v2 (2026-10-26). See apps/admin/AGENTS.md. */
import type { CSSProperties } from "react";
import type { LucideIcon } from "lucide-react";
import { Doodles } from "./doodles";
import { cx } from "./cx";

export type IconProps = {
  icon: LucideIcon;
  /** 14–18 for small things. Default 16. */
  size?: number;
  color?: string;
  /** 1.75 by default; 1.8 in the sidebar, 2 on a primary fill. */
  stroke?: number;
  /** Gives the icon an accessible name; without it the icon is decorative. */
  label?: string;
  className?: string;
};

/** Small things: buttons, nav rows, filters, inputs (Lucide 0.460). */
export function Icon({ icon: Glyph, size = 16, color, stroke = 1.75, label, className }: IconProps) {
  return (
    <span
      className={cx("rd-icon", className)}
      style={{ width: size, height: size }}
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    >
      <Glyph size={size} strokeWidth={stroke} style={color ? { color } : undefined} />
    </span>
  );
}

const badgeCache = new Map<string, string>();

export type BadgeShape =
  | "circle"
  | "rounded"
  | "squircle"
  | "hexagon"
  | "scallop"
  | "flower"
  | "blob";

type BadgeProps = {
  /** A Doodle id: "chart", "team", "register", "gear", "map", "bill"… */
  id: string;
  /** 36 by default. Page heads 36–40, tiles 36, nav parents 24, cells 28. */
  size?: number;
  shape?: BadgeShape;
  shadow?: boolean;
  hand?: boolean;
  label?: string;
  className?: string;
  style?: CSSProperties;
};

/**
 * Big things: a hand-drawn Doodle badge. The SVG markup comes from the
 * vendored library (static, ours), so it is set as HTML rather than parsed.
 */
export function Badge({
  id,
  size = 36,
  shape = "circle",
  shadow = true,
  hand = true,
  label,
  className,
  style,
}: BadgeProps) {
  const key = [id, shape, hand, shadow].join("|");
  let html = badgeCache.get(key);
  if (html === undefined) {
    const index = Doodles.badges.findIndex((badge) => badge.id === id);
    html =
      index < 0
        ? ""
        : Doodles.source(Doodles.badges[index].svg, 1.6, null, {
            hand,
            seed: index + 3,
            wobble: 0,
            anim: false,
            shape,
            shadow: { on: shadow, dist: 2, angle: 45, color: "#1d1d1f" },
            ring: { on: true, width: 2.4, color: "#1d1d1f" },
          });
    badgeCache.set(key, html);
  }
  return (
    <span
      className={cx("rd-badge", className)}
      style={{ width: size, height: size, ...style }}
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
